/* Atlas v3 — "The Wire" (Signals): a borderless chronological feed of
   signal events grouped by day. (The swipe-through Triage mode was never
   wired to the feed and was deleted 2026-08-15 — resurrect from git if a
   triage flow is ever wanted.)

   Brings its own shell (TickerStrip + BottomNav) like Overview/MyStocks —
   NOT wrapped in the old AppLayout. No starfield (Overview-only).

   Design: borderless feed — hairline-divided rows + whitespace, NOT
   bordered cards (except the WhileYouSlept + Constellation feature cards,
   which are intentional cards). True-black base, amber line/label only,
   green/red for direction only.

   ⚠ COLOR EXCEPTION: WhileYouSleptCard keeps its INDIGO treatment — one
   of the two sanctioned exceptions to the no-violet/blue rule (per
   CURRENT-STATE.md). Do NOT flatten it to amber.

   Data wiring:
     • signal items   ← useSignalsRecent() (signal_events: ticker,
       signal_type, direction, headline, dollar_amount, signal_date,
       details). Mapped to the mockup's feed-item shape.
     • constellation  ← useConstellationAlerts() — compact strip; self-hides
       when there are no alerts.
     • news items     ← no clean news read endpoint/hook exists in the repo
       (grep news_articles/useNews/api news → nothing). The feed is
       signals-only for now; the NEWS filter shows an empty state.
       // TODO(data): merge news_articles into the feed when an endpoint exists.
     • WhileYouSlept  ← headline derived from the most significant overnight
       signal event. The index strip proxies ES/NQ/YM with SPY/QQQ/DIA
       extended-hours moves (useLivePrices → Polygon snapshot todaysChangePerc);
       shown only inside an ext-hours window (extendedSession), hidden overnight
       and in regular session rather than faking it. No futures feed (Polygon
       bills index futures separately).
     • conviction     ← signal_events.details.conviction if present, else
       null (ConvHalo only renders when a value exists).
     • ★ add-to-watch ← useWatchlists().addTicker into the user's first list,
       with a local toast; falls back to a local-only star when signed out. */
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  TickerCtx, intent, SignalBadge, signalWriteup, signalToSheet,
} from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockLogo } from '@/v2/components/StockLogo'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { NewsSheet } from './NewsSheet'
import { useSignalsRecent, useConstellationAlerts, useNews } from '@/v2/hooks/useSupabaseData'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useLivePrices } from '@/v2/hooks/useLivePrices'

const REDUCE =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

/* ── monoNums — wrap numeric runs in a headline so figures render in tabular
   mono instead of Instrument Serif (whose `1`/`l` collide and whose stats lose
   crispness). Matches $-prefixed, decimal, %, and ×/x/K/M/B-suffixed tokens;
   the `(?!-)` skips label-like "8-K". Returns an array of strings + <span>s. */
const NUM_RE = /(\$?\d[\d,]*(?:\.\d+)?(?:\s?[%×xKMB])?)(?!-)/g
function monoNums(text) {
  if (typeof text !== 'string') return text
  // String.split with a capturing group interleaves matches on ODD indices —
  // key off parity, NOT a /\d/ test (which would wrap a whole "8-K"-only string).
  return text.split(NUM_RE).map((part, i) =>
    i % 2 === 1
      ? <span key={i} style={{ ...Mono, fontStyle: 'normal', fontSize: '0.9em', fontVariantNumeric: 'tabular-nums' }}>{part}</span>
      : part,
  )
}

const FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'signals', label: 'SIGNALS' },
  { id: 'news', label: 'NEWS' },
]

const SIGNAL_TYPE_LABELS = {
  momentum: 'Momentum', insider_trade: 'Insider', options_unusual: 'Options',
  '8k_filing': '8-K', earnings: 'Earnings', nvda_cascade: 'Cascade',
  ceo_cfo_transition: 'Exec', secondary_offering: 'Offering',
  datacenter_power: 'Power', news_sentiment_shift: 'Buzz',
}

/* ── normalize a signal_event row → the mockup's feed-item shape ── */
function sentimentFromDir(direction) {
  if (direction === 'bullish') return 'bullish'
  if (direction === 'bearish') return 'bearish'
  return 'neutral'
}
function eventToItem(ev) {
  const sentiment = sentimentFromDir(ev.direction)
  const d = ev?.details || ev?.detail || {}
  // Conviction lives in the top-level `confidence` column (0-100), NOT detail —
  // the mosaic scorer writes it there. Fall back to a legacy detail.conviction
  // just in case. The >1 ? /100 path normalizes 0-100 → the 0-1 ConvHalo expects.
  const conv = typeof ev.confidence === 'number' ? ev.confidence : d.conviction
  // Options magnitude tiebreaker: confidence saturates at 90 for most options,
  // so rank the genuinely lopsided flow first. max(cpr, 1/cpr) is large for a
  // very call- OR put-heavy ratio, ~1 for balanced.
  const cpr = Number(d.call_put_ratio)
  const mag = Number.isFinite(cpr) && cpr > 0 ? Math.max(cpr, 1 / cpr) : 0
  return {
    kind: 'signal',
    id: ev.id,
    t: ev.ticker,
    n: d.company || ev.ticker,
    type: ev.signal_type,
    sentiment,
    dir: sentiment === 'bearish' ? -1 : 1,
    conv: typeof conv === 'number' ? (conv > 1 ? conv / 100 : conv) : null,
    mag, // secondary sort key within equal conviction
    // Real-money magnitude — total_premium exists on options signals since the
    // 2026-07-02 quality gates; 0 for older rows / other signal types.
    prem: Number(d.total_premium) || 0,
    headline: signalWriteup(ev),
    why: d.summary || d.why || null,
    ts: ev.signal_date,
    time: relTime(ev.signal_date),
  }
}

/* ── normalize a news_articles row → feed-item shape (sentiment null) ── */
function articleToItem(a) {
  return {
    kind: 'news',
    id: `news-${a.id}`,
    t: a.ticker,
    n: a.ticker,
    type: 'news',
    sentiment: null, // news carries no sentiment — render guards on this
    dir: null,
    conv: null,
    headline: a.headline,
    why: a.source || null, // publisher as the sub-line
    url: a.url || null,    // source link — escape hatch at the bottom of the sheet
    summary: a.summary || null, // provider blurb — the in-app NewsSheet content
    image: a.image_url || null,
    ts: a.published_at,
    time: relTime(a.published_at),
  }
}

/* ── time helpers ── */
function relTime(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
function ageHours(iso) {
  if (!iso) return 9999
  return (Date.now() - new Date(iso).getTime()) / 3.6e6
}

function sentimentArrow(s) {
  if (s === 'bullish') return { glyph: '↑', color: 'hsl(var(--studio-success))' }
  if (s === 'bearish') return { glyph: '↓', color: 'hsl(var(--studio-danger))' }
  return { glyph: '→', color: 'hsl(var(--studio-accent-soft))' }
}

/* ── Conviction score — bare tabular value. The amber progress ring was removed:
   options confidence saturates at 90, so every arc rendered as the same near-full
   circle that read as decorative rather than data (and as hollow at 22px). ── */
function ConvHalo({ conv }) {
  return (
    <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.04em', color: 'hsl(var(--studio-accent-soft))', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
      {Math.round(conv * 100)}
    </span>
  )
}

// ET extended-hours session label, or null outside ext-hours / on weekends.
// Pre-market 4:00-9:30 ET, after-hours 16:00-20:00 ET. The SPY/QQQ/DIA ext-
// hours proxy is only honest in these windows — overnight (20:00-04:00) the
// ETFs don't trade, so we hide the strip rather than show stale/fake numbers.
function extendedSession() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
  const day = et.getDay()
  if (day === 0 || day === 6) return null
  const hour = et.getHours() + et.getMinutes() / 60
  if (hour >= 4 && hour < 9.5) return 'PRE-MARKET'
  if (hour >= 16 && hour < 20) return 'AFTER HRS'
  return null
}

/* ── WhileYouSlept — overnight summary card (KEEP INDIGO) ────────────── */
function WhileYouSleptCard({ overnight, proxy }) {
  const headline = overnight?.headline || 'Quiet overnight — no fresh signals'
  return (
    <div style={{
      margin: '16px 20px 0',
      padding: '18px 20px',
      background: [
        'radial-gradient(ellipse 55% 60% at 92% 85%, hsl(38 45% 18% / 0.55) 0%, transparent 70%)',
        'radial-gradient(ellipse 60% 65% at 8% 5%, hsl(252 65% 68% / 0.42) 0%, transparent 65%)',
        'linear-gradient(157deg, hsl(252 52% 16%) 0%, hsl(248 40% 10%) 50%, hsl(240 22% 6%) 100%)',
      ].join(','),
      border: '1px solid hsl(252 48% 38% / 0.6)',
      borderRadius: 'var(--studio-radius)',
      position: 'relative', overflow: 'hidden',
    }}>
      <svg viewBox="0 0 350 80" aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.5 }}>
        {Array.from({ length: 22 }).map((_, i) => (
          <circle key={i} cx={(i * 53) % 350} cy={((i * 29) % 64) + 8} r={(i % 3) * 0.5 + 0.5} fill="hsl(232 44% 90%)" />
        ))}
      </svg>
      <div style={{ position: 'relative' }}>
        <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-accent))', marginBottom: 8 }}>
          WHILE YOU SLEPT
        </div>
        <div style={{ ...Display, fontSize: 20, color: 'hsl(var(--studio-text))', lineHeight: 1.25, marginBottom: 12 }}>
          {monoNums(headline)}
        </div>
        {/* Index ext-hours proxy (SPY/QQQ/DIA via Polygon snapshot). Rendered
            only inside an extended-hours window with full data; hidden overnight
            and in regular session rather than showing stale/fake numbers. */}
        {proxy && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))' }}>{proxy.session}</span>
            {proxy.rows.map((r) => (
              <span key={r.label} style={{ ...Mono, fontSize: 12, color: `hsl(var(${r.pct >= 0 ? '--studio-success' : '--studio-danger'}))` }}>
                {r.label} {r.pct >= 0 ? '+' : ''}{r.pct.toFixed(1)}%
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Constellation — compact multi-signal corroboration strip ────────── */
function ConstellationStrip({ alert, openTicker }) {
  const [hot, setHot] = useState(false)
  if (!alert) return null
  const n = alert.signal_count ?? (alert.types?.length || 0)
  return (
    <div style={{ padding: '4px 20px 2px' }}>
      <div onClick={() => openTicker(alert.ticker)}
        onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
          background: hot ? 'hsl(var(--studio-accent) / 0.1)' : 'hsl(var(--studio-accent) / 0.06)',
          border: '1px solid hsl(var(--studio-accent) / 0.35)', borderRadius: 'var(--studio-radius)',
          padding: '8px 12px', transition: 'background 140ms var(--studio-ease)',
        }}>
        <span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-accent-soft))', flexShrink: 0 }}>✦</span>
        <StockLogo ticker={alert.ticker} size={18} rounded={4} />
        <span style={{ ...Mono, fontSize: 11, letterSpacing: '0.05em', color: 'hsl(var(--studio-text))', flexShrink: 0 }}>{alert.ticker}</span>
        <span style={{ ...Sans, fontSize: 11.5, color: 'hsl(var(--studio-text-muted))', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {n} signals aligned this week
        </span>
        <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent-soft))', flexShrink: 0 }}>VIEW →</span>
      </div>
    </div>
  )
}

/* ── filter chips (sticky) ── */
function FilterChips({ active, onChange, counts }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'hsl(var(--studio-background))',
      borderBottom: '1px solid hsl(var(--studio-border) / 0.5)',
      padding: '10px 20px', display: 'flex', gap: 7, overflowX: 'auto', scrollbarWidth: 'none',
    }}>
      {FILTERS.map((f) => {
        const on = active === f.id
        return (
          <button key={f.id} onClick={() => onChange(f.id)} style={{
            flex: '0 0 auto', ...Mono, fontSize: 10, letterSpacing: '0.1em',
            padding: '6px 5px', cursor: 'pointer', whiteSpace: 'nowrap',
            background: 'transparent', border: 'none',
            borderBottom: on ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent',
            color: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))',
            transition: 'all 160ms var(--studio-ease)',
          }}>{f.label} <span style={{ opacity: 0.6 }}>{counts[f.id]}</span></button>
        )
      })}
    </div>
  )
}

/* ── signal-type sub-filter chips (only under SIGNALS) ── */
function SignalTypeChips({ active, onChange, types }) {
  const all = ['all', ...types]
  return (
    <div className="atlas-scroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '9px 20px 10px', scrollbarWidth: 'none', borderBottom: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      {all.map((t) => {
        const on = active === t
        const label = t === 'all' ? 'ALL TYPES' : (SIGNAL_TYPE_LABELS[t] || t).toUpperCase()
        return (
          <button key={t} onClick={() => onChange(t)} style={{
            flex: '0 0 auto', ...Mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
            background: on ? 'hsl(var(--studio-surface-2))' : 'transparent',
            color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text-subtle))',
            border: `1px solid ${on ? 'hsl(var(--studio-border))' : 'hsl(var(--studio-border) / 0.5)'}`,
            transition: 'all 140ms var(--studio-ease)',
          }}>{label}</button>
        )
      })}
    </div>
  )
}

/* ── single borderless feed row ── */
function WireFeedCard({ item, openTicker, openNews, first, starred, onStar }) {
  const [hot, setHot] = useState(false)
  const arrow = sentimentArrow(item.sentiment)
  // News rows carry no sentiment → neutral pip; signals keep amber(up)/red(down).
  const borderCol = item.sentiment == null
    ? 'hsl(var(--studio-text-subtle))'
    : item.dir > 0 ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-danger))'
  // News rows read the provider blurb (something to actually read); signals keep
  // their write-up summary. Source/publisher moves up to the meta line.
  const subline = item.kind === 'news' ? (item.summary || item.why) : item.why

  function toggleStar(e) {
    e.stopPropagation()
    if (!starred) onStar(item.t)
  }

  // Both news and signals open the in-app reader sheet (NewsSheet): news shows
  // the provider blurb, signals show their write-up. The ticker stays reachable
  // from inside the sheet (logo/ticker → drawer), so this also sidesteps the
  // old no-op when a news ticker wasn't in the universe.
  function onRowClick() {
    openNews(signalToSheet(item))
  }

  return (
    <div
      onClick={onRowClick}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{
        position: 'relative', padding: '15px 6px 14px 16px',
        borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.4)', cursor: 'pointer',
      }}>
      {/* press highlight */}
      <div style={{ position: 'absolute', inset: '5px -8px', borderRadius: 9, background: 'hsl(var(--studio-surface-2))', opacity: hot ? 0.55 : 0, transition: 'opacity 140ms var(--studio-ease)', pointerEvents: 'none' }} />
      {/* floating sentiment pip */}
      <span style={{ position: 'absolute', left: -1, top: '50%', transform: 'translateY(-50%)', width: 3, height: 26, borderRadius: 999, background: borderCol }} />
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <StockLogo ticker={item.t} size={24} rounded={4} />
            <span style={{ ...Mono, fontSize: 12.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text))' }}>{item.t}</span>
            {/* extra tickers this article was tagged to (deduped news only) */}
            {item.kind === 'news' && item.tickers && item.tickers.slice(1, 4).map((tk) => (
              <span key={tk} style={{ ...Mono, fontSize: 9, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', padding: '2px 5px', borderRadius: 4, border: '1px solid hsl(var(--studio-border) / 0.5)' }}>{tk}</span>
            ))}
            {item.kind === 'news' && item.tickers && item.tickers.length > 4 && (
              <span style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))' }}>+{item.tickers.length - 4}</span>
            )}
            <SignalBadge type={item.type} />
            {item.conv != null && <ConvHalo conv={item.conv} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right' }}>
              {item.sentiment != null && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                  <span style={{ ...Mono, fontSize: 14, color: arrow.color, lineHeight: 1 }}>{arrow.glyph}</span>
                  <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', color: arrow.color }}>{item.sentiment.toUpperCase()}</span>
                </div>
              )}
              {item.kind === 'news' && item.why && (
                <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.why}</div>
              )}
              <div style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>{item.time}</div>
            </div>
            <button onClick={toggleStar} aria-label={starred ? 'On your Radar' : 'Add to Radar'}
              style={{ width: 28, height: 28, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'hsl(var(--studio-accent))', transition: 'all 140ms var(--studio-ease)' }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>{starred ? '★' : '☆'}</span>
            </button>
          </div>
        </div>

        {/* headline + subline, with the article thumbnail riding the right edge
            on news rows that have one (news_articles.image_url — captured by
            fetch-news all along, first rendered 2026-07). Fixed box = no layout
            shift; NewsThumb hides itself if the provider URL 404s. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...Display, fontStyle: 'normal', fontSize: 18, lineHeight: 1.3, color: 'hsl(var(--studio-text))', marginBottom: subline ? 6 : 0, textWrap: 'pretty' }}>
              {monoNums(item.headline)}
            </div>
            {subline && (
              <div style={{ ...Sans, fontSize: 12.5, lineHeight: 1.5, color: 'hsl(var(--studio-text-muted))', display: '-webkit-box', WebkitLineClamp: item.kind === 'news' ? 2 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{subline}</div>
            )}
          </div>
          {item.kind === 'news' && item.image && <NewsThumb src={item.image} />}
        </div>
      </div>
    </div>
  )
}

/* ── news thumbnail — fixed 68px box on the row's right edge; removes itself
      if the provider image fails to load so a dead URL never leaves a hole ── */
function NewsThumb({ src }) {
  const [dead, setDead] = useState(false)
  if (dead) return null
  return (
    <img
      src={src} alt="" loading="lazy" onError={() => setDead(true)}
      style={{ width: 68, height: 68, flexShrink: 0, borderRadius: 8, objectFit: 'cover', border: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-surface-2))' }}
    />
  )
}

/* ── feed skeleton ── */
function FeedSkeleton() {
  return (
    <div style={{ padding: '6px 20px 0' }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ padding: '15px 6px 14px 16px', borderTop: i === 0 ? 'none' : '1px solid hsl(var(--studio-border) / 0.4)', position: 'relative' }}>
          <span style={{ position: 'absolute', left: -1, top: '50%', transform: 'translateY(-50%)', width: 3, height: 26, borderRadius: 999, background: 'hsl(var(--studio-border) / 0.6)' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <div className="atlas-skeleton" style={{ width: 24, height: 24, borderRadius: 4 }} />
            <div className="atlas-skeleton" style={{ width: 48, height: 12, borderRadius: 3 }} />
            <div className="atlas-skeleton" style={{ width: 44, height: 12, borderRadius: 10 }} />
          </div>
          <div className="atlas-skeleton" style={{ width: '85%', height: 14, borderRadius: 3, marginBottom: 7 }} />
          <div className="atlas-skeleton" style={{ width: '62%', height: 11, borderRadius: 3 }} />
        </div>
      ))}
    </div>
  )
}

/* ── day header ── */
function WireDayHead({ label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px 8px' }}>
      <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-muted))' }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: 'hsl(var(--studio-border) / 0.5)' }} />
      <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>{count} {count === 1 ? 'ITEM' : 'ITEMS'}</span>
    </div>
  )
}

/* Market Mood strip removed 2026-07-02 (Ben: pointless, noisy, took up
   space). Buzz (news_sentiment_shift) signals stay excluded from the row
   feed below — without the strip they simply don't render on The Wire. */

/* ── feed view ── */
function WireFeed({ items, loading, alert, overnight, proxy, openTicker, openNews, starred, onStar }) {
  const [filter, setFilter] = useState('all')
  const [typeF, setTypeF] = useState('all')
  useEffect(() => { if (filter !== 'signals' && typeF !== 'all') setTypeF('all') }, [filter, typeF])

  // Buzz (news_sentiment_shift) signals are excluded from the row feed — they
  // carry no headline, only a bullish %, so stacked as cards they were a
  // monotonous wall (their Market Mood strip was removed 2026-07-02 as noise).
  // `feed` is everything else: real news + the meatier signals.
  const isBuzz = (i) => i.kind === 'signal' && i.type === 'news_sentiment_shift'
  const feed = useMemo(() => items.filter((i) => !isBuzz(i)), [items])

  // Signal-type sub-filter only lists the meatier signal types (buzz excluded).
  const sigTypes = useMemo(() => [...new Set(feed.filter((i) => i.kind === 'signal').map((i) => i.type).filter(Boolean))], [feed])
  // Counts reflect what actually renders (feed), not the raw item set.
  const counts = {
    all: feed.length,
    signals: feed.filter((i) => i.kind === 'signal').length,
    news: feed.filter((i) => i.kind === 'news').length,
  }

  let view = feed
  if (filter === 'signals') view = feed.filter((i) => i.kind === 'signal')
  else if (filter === 'news') view = feed.filter((i) => i.kind === 'news')
  if (filter === 'signals' && typeF !== 'all') view = view.filter((i) => i.type === typeF)
  // Tiered ordering for TODAY: the loudest signals (high conviction) lead, then
  // everything else stays newest-first underneath — so a 90-conviction options
  // sweep from the morning doesn't get buried under afternoon noise. Scoped to
  // today's items only (the LOUD bump doesn't reach back into EARLIER, so a big
  // read from earlier in the week can't permanently camp at the top). News rows
  // carry no conviction → they're always in the chronological tier.
  const LOUD = 0.75 // conviction floor (0-1, i.e. ≥75) to earn the front of the feed
  const byAge = (a, b) => ageHours(a.ts) - ageHours(b.ts)
  // Loudest first: conviction desc, then options magnitude desc (most lopsided
  // call/put flow — breaks the 90-cap tie so an 11x sweep beats a 1.5x), then newest.
  const byLoud = (a, b) => (b.conv - a.conv) || ((b.mag || 0) - (a.mag || 0)) || byAge(a, b)
  const isLoud = (i) => i.conv != null && i.conv >= LOUD
  // Lead bucket = the freshest signal batch, measured from the NEWEST item rather
  // than the wall clock — signals land in one daily cron run (~5pm ET), so during
  // the day the latest batch is yesterday's. Anchoring to the newest item keeps the
  // loudest-first lead working all day instead of going empty until the evening run.
  const newestMs = view.reduce((m, i) => Math.max(m, new Date(i.ts || 0).getTime()), 0)
  const ageFromNewest = (i) => (newestMs - new Date(i.ts || 0).getTime()) / 3.6e6
  const lead = view.filter((i) => ageFromNewest(i) < 24)
  const earlier = view.filter((i) => ageFromNewest(i) >= 24)
  const leadSorted = [
    ...lead.filter(isLoud).sort(byLoud),
    ...lead.filter((i) => !isLoud(i)).sort(byAge),
  ]
  // Label the lead group honestly: "TODAY" only if the freshest batch really is today.
  const leadLabel = newestMs && (Date.now() - newestMs) / 3.6e6 < 24 ? 'TODAY' : 'LATEST'
  const groups = [
    [leadLabel, leadSorted],
    ['EARLIER THIS WEEK', [...earlier].sort(byAge)],
  ].filter(([, a]) => a.length)

  return (
    <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      <WhileYouSleptCard overnight={overnight} proxy={proxy} />
      {filter !== 'news' && <ConstellationStrip alert={alert} openTicker={openTicker} />}
      <FilterChips active={filter} onChange={setFilter} counts={counts} />
      {filter === 'signals' && sigTypes.length > 0 && <SignalTypeChips active={typeF} onChange={setTypeF} types={sigTypes} />}
      {loading ? <FeedSkeleton /> : (
        <div>
          {groups.map(([label, arr]) => (
            <div key={label}>
              <WireDayHead label={label} count={arr.length} />
              <div style={{ padding: '0 20px' }}>
                {arr.map((item, i) => (
                  <WireFeedCard key={item.id ?? `${item.t}-${i}`} item={item} openTicker={openTicker} openNews={openNews} first={i === 0}
                    starred={starred.has(item.t)} onStar={onStar} />
                ))}
              </div>
            </div>
          ))}
          {!groups.length && (
            <div style={{ padding: '48px 20px', textAlign: 'center', ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>
              {filter === 'news' ? 'NO NEWS YET — CHECK BACK SOON' : 'NOTHING MATCHES THIS FILTER'}
            </div>
          )}
          <div style={{ padding: '28px 20px', ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center' }}>
            ATLAS INTELLIGENCE · UPDATED DAILY
          </div>
        </div>
      )}
    </div>
  )
}

/* ── body ── */
function WireBody() {
  const { openTicker } = useStockDetail()
  // Recent signals (not strictly today's — the daily ingest cron runs ~5pm ET, so
  // a today-only read shows "0 signals" all morning). The feed buckets these into
  // a freshest-batch lead group + earlier-this-week below.
  const { events, loading } = useSignalsRecent(200)
  const { news } = useNews()
  const { alerts } = useConstellationAlerts()
  const { watchlists, addTicker } = useWatchlists()

  const [toast, setToast] = useState(null)
  const [newsItem, setNewsItem] = useState(null) // open NewsSheet
  // local star set — tracks what the user has ★'d this session (so the
  // glyph fills immediately even before the watchlist refresh round-trips).
  const [starred, setStarred] = useState(() => new Set())

  // Signals drive Triage + the header count; the Feed merges signals + news.
  const signalItems = useMemo(() => (events || []).map(eventToItem), [events])
  // Dedup news by URL — fetch-news writes one news_articles row per (ticker,url),
  // so a market-wide article tagged to N names arrived as N identical cards.
  // Collapse to one card per article; carry the extra tickers as tags.
  const newsItems = useMemo(() => {
    const byUrl = new Map()
    for (const a of (news || [])) {
      const item = articleToItem(a)
      const key = item.url || item.id
      const ex = byUrl.get(key)
      if (ex) {
        if (item.t && !ex.tickers.includes(item.t)) ex.tickers.push(item.t)
      } else {
        byUrl.set(key, { ...item, tickers: item.t ? [item.t] : [] })
      }
    }
    return [...byUrl.values()]
  }, [news])
  const items = signalItems // header/overnight reference signals
  const feedItems = useMemo(() => [...signalItems, ...newsItems], [signalItems, newsItems])
  const alert = alerts?.[0] || null

  // Overnight hero — the HIGHEST-CONVICTION signal from the overnight window
  // (was: whatever came first in feed order, i.e. merely the newest). Ties
  // break on real-money magnitude (options total premium — deliberately NOT
  // raw call/put ratio, which is loudest on the thinnest chains), then
  // recency. Falls back to the full item set when the window is empty.
  const overnight = useMemo(() => {
    // Overnight window ONLY. This used to fall back to the full item set when
    // the window was empty, which promoted a DAYTIME signal under a "while you
    // slept" banner — and since the feed below renders that same item, the card
    // and a row two positions down carried the identical sentence verbatim.
    // A quiet night should read as a quiet night: the caller already falls back
    // to "Quiet overnight — no fresh signals".
    const pool = items.filter((i) => ageHours(i.ts) >= 8 && ageHours(i.ts) <= 18)
    if (!pool.length) return null
    const lead = [...pool].sort((a, b) =>
      ((b.conv ?? 0) - (a.conv ?? 0)) ||
      ((b.prem ?? 0) - (a.prem ?? 0)) ||
      (ageHours(a.ts) - ageHours(b.ts))
    )[0]
    // Summarise, don't quote. This used to append `lead.headline` verbatim —
    // but the feed below renders that same item, so the card and a row a few
    // hundred pixels down carried the identical sentence word for word. The
    // count and which one ranked highest are the things the chronological feed
    // can't tell you; the substance stays on the row where it belongs.
    const mood = lead.sentiment === 'bearish' ? 'under pressure' : 'in play'
    return {
      headline: pool.length > 1
        ? `${pool.length} signals overnight — ${lead.t} the standout.`
        : `One signal overnight — ${lead.t} ${mood}.`,
    }
  }, [items])

  // Index ext-hours proxy for the WhileYouSlept strip: SPY/QQQ/DIA move via the
  // Polygon snapshot (change1d carries pre/post-market via todaysChangePerc).
  // Only built inside an extended-hours window with all three present — else
  // null → the strip hides (no stale/fake overnight numbers).
  const { prices: idxPx } = useLivePrices(['SPY', 'QQQ', 'DIA'])
  const proxy = useMemo(() => {
    const session = extendedSession()
    if (!session) return null
    const rows = [['SPY', 'SPY'], ['QQQ', 'QQQ'], ['DIA', 'DIA']]
      .map(([t, label]) => ({ label, pct: idxPx[t]?.change1d }))
    if (rows.some((r) => typeof r.pct !== 'number')) return null
    return { session, rows }
  }, [idxPx])

  function onStar(ticker) {
    const tk = String(ticker).toUpperCase()
    setStarred((prev) => new Set(prev).add(tk))
    // add to the user's first list if one exists (signed in); otherwise the
    // star is local-only this session. // TODO(data): a default-list helper.
    const listId = watchlists?.[0]?.id
    if (listId) addTicker(listId, tk)
    setToast(`${tk} added to Radar`)
    setTimeout(() => setToast((t) => (t && t.startsWith(tk) ? null : t)), 1700)
  }

  const signalCount = items.length
  const newsCount = newsItems.length
  const wireToday = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>

        {/* page header */}
        <div style={{ flexShrink: 0, padding: '20px 20px 13px', borderBottom: '1px solid hsl(var(--studio-border) / 0.5)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...Display, fontSize: 27, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>The Wire</div>
              <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 6 }}>
                {signalCount} SIGNALS · {newsCount} NEWS · {wireToday}
              </div>
            </div>
            <SearchPill />
          </div>
        </div>

        {/* body */}
        <WireFeed items={feedItems} loading={loading} alert={alert} overnight={overnight} proxy={proxy} openTicker={openTicker} openNews={setNewsItem} starred={starred} onStar={onStar} />

        {toast && (
          <div style={{ position: 'absolute', bottom: 96, left: '50%', transform: 'translateX(-50%)', background: 'hsl(var(--studio-surface))', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent))', padding: '8px 18px', borderRadius: 999, ...Mono, fontSize: 10.5, letterSpacing: '0.08em', whiteSpace: 'nowrap', zIndex: 99, pointerEvents: 'none' }}>
            {toast}
          </div>
        )}

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
        {newsItem && <NewsSheet item={newsItem} onClose={() => setNewsItem(null)} onTicker={openTicker} />}
      </div>
    </TickerCtx.Provider>
  )
}

export function Wire() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <WireBody />
      </StockDetailProvider>
    </div>
  )
}
